// errors.data.ts

import { RequestTypeShort, StoreId } from '@/types';
import { MpfTenantInfoProps } from '@/lib/components/MpfTenantInfo/MpfTenantInfo.props';

export interface ErrorMetadata {
  title?: string;
  messageNhsApp?: string;
  messageNhsAppWeb?: string;
  details?: string;
  route?: string;
  key?: string;
  showStartButton?: boolean;
  showBackButton?: boolean;
  redirectToError?: boolean;
  components?: ErrorComponent[];
  requestType?: RequestTypeShort | StoreId;
}

export interface ErrorComponent {
  name: string;
  props: MpfTenantInfoProps;
}

type RouteMap<T extends string | number | symbol> = Record<T, ErrorMetadata>;

export const errorMessages: Record<string, string> = {
  'unknown-error': 'Unknown error. No code was matched',

  // Domain / validation errors
  'unable-to-send-verification-code':
    'For security reasons, you can’t request another code right now.',

  'organisation-not-configured-to-send-verification-code':
    'We’re unable to continue your request because your GP practice isn’t set up to send confirmation codes.',

  'incorrect-verification-code-input': 'You have entered in an incorrect code. Please try again.',

  'verification-code-expired': 'Your code has expired. Please request a new one to continue.',

  'too-many-verification-code-failed-attempts': 'You’ve entered an incorrect code too many times.',

  'cannot-validate-multiple-verification-codes':
    'This verification code has already been used. Press ‘Start again’ to request a new one.',

  'verification-code-not-found': 'We couldn’t verify your code.',

  'patient-has-no-email-address-or-mobile-number':
    'It’s not possible to continue with your request online because we do not have a valid email address or mobile number on record for you.',

  'proxy-patient-has-no-email-address-or-mobile-number':
    'It’s not possible to continue with your request online because we do not have a valid email address or mobile number on record for this patient.',

  'patient-has-no-email-address':
    'It’s not possible to continue with your request online because we do not have a valid email address on record for you.',

  'patient-has-invalid-email-address':
    'It’s not possible to continue with your request online because we do not have a valid email address on record for you.',

  'patient-has-no-mobile-number':
    'It’s not possible to continue with your request online because we do not have a valid mobile number on record for you.',

  'patient-has-invalid-mobile-number':
    'It’s not possible to continue with your request online because we do not have a valid mobile number on record for you.',

  'patient-has-invalid-email-address-and-mobile-number':
    'It’s not possible to continue with your request online because we do not have a valid email address or mobile number on record for you.',

  'logged-in-patient-not-found':
    'It was not possible to match the information provided to a patient record at this practice.',

  'nhs-login-logged-in-patient-not-found':
    'It was not possible to match the NHS login user to a patient record at this practice.',

  'single-patient-not-found':
    'It was not possible to match the information provided to a patient record at this practice.',

  'no-active-patient-found':
    'It was not possible to match the information provided to a patient record at this practice.',

  'request-submission-patient-details-not-complete':
    'It was not possible to submit your request online due to an issue with your patient details.',

  'no-repeat-medication-found': 'No repeat medications are available to request.',

  'unsupported-file-type':
    'Please upload in one of the following formats: JPEG, PNG & Word.',

  'eps-unavailable-exception':
    'We’re having trouble connecting to the NHS Electronic Prescription Service. Please try again or come back later.',

  // Session / auth errors
  'user-session-invalid':
    'User not authorised to view this page. If you were submitting a request, it was not sent.',

  'sso-user-session-invalid':
    'There was an issue with NHS login. This may be a temporary problem. \n\n' +
    'Go back and try logging in again. If you cannot log in, try again later.',

  'nhs-login-user-verification-level-too-low':
    'It’s not possible to submit your request online with NHS login because your identity isn’t verified.\n\n' +
    'To learn how to verify your identity, visit the NHS login help centre.\n\n' +
    'You can submit a request now by entering your personal details instead.',

  'nhs-login-sso-user-verification-level-too-low':
    'It’s not possible to submit your request online with NHS login because your identity isn’t verified.',

  // Form availability
  'repeat-medication-request-form-unavailable':
    'Online repeat prescription requests are temporarily suspended. If you entered any information, it was not sent to your practice.',

  'admin-request-form-unavailable':
    'Online admin requests are currently unavailable. If you entered any information, it was not sent to your practice.\n' +
    'Please come back when requests are open.\n\n',

  'medical-request-form-unavailable':
    'Online medical requests are currently unavailable. If you entered any information, it was not sent to your practice.\n' +
    'Please come back when requests are open.\n\n',

  // NHS SSO general error
  'unknown-error-nhs-param':
    'There was an issue with NHS login. This may be a temporary problem. \n' +
    'Go back and try logging in again. If you cannot log in, try again later.',

  access_denied:
    'The service provider needs your NHS login information to verify your identity.\n' +
    'To continue, go back and agree to share your NHS login information. ',

  // to match 'ConsentNotGiven'
  'consent-not-given-nhs':
    'The service provider needs your NHS login information to verify your identity.\n\n' +
    'To continue, go back and agree to share your NHS login information.',

  consent_required:
    'The service provider needs your NHS login information to verify your identity.\n\n' +
    'To continue, go back and agree to share your NHS login information.',

  identity_proofing_level:
    'It’s not possible to submit your request online with NHS login because your identity isn’t verified. ',

  'patient-portal-form-closed':
    'Online requests are currently unavailable. If you entered any information, it was not sent to your practice.\n' +
    'Please come back when requests are open.\n',

  'nhs-sso-generic':
    'There was an issue with NHS login. This may be a temporary problem. \n' +
    'Go back and try logging in again. If you cannot log in, try again later.',

  'medicus-sso-generic':
    'This may be a temporary problem. Go back and try logging in again. If you cannot log in, try again later.',

  'nhs-app-sso-session-expired':
    'Your sign in has expired. This happens if the page was reloaded, or if too long passed while you were signing in.\n\n' +
    'Go back to the NHS App and start your request again.',

  'browser-action-issue':
    'The page reloaded and your progress could not be saved. Go back and try again.',
};

// NB. extra erro management details joined by key here
export const errorMessageMeta: Partial<RouteMap<keyof typeof errorMessages>> = {
  'unknown-error-nhs-param': {
    title: 'There is a problem',
    messageNhsAppWeb:
      'There was an issue with NHS login. This may be a temporary problem. \n' +
      'Close this tab and try logging in again. If you cannot log in, try again later.',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showNHSAppVerificationDetails: false,
          showNHSAppDetails: true,
        },
      },
    ],
  },
  'browser-action-issue': {
    title: 'The page reloaded unexpectedly',
    showStartButton: false,
    messageNhsAppWeb:
      'The page reloaded and your progress could not be saved. Close this tab and try again',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showNHSAppVerificationDetails: false,
          showNHSAppDetails: true,
        },
      },
    ],
  },

  'nhs-sso-generic': {
    title: 'There is a problem',
    messageNhsAppWeb: 'There was an issue with NHS login. This may be a temporary problem.',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showNHSAppVerificationDetails: false,
          showNHSAppDetails: true,
        },
      },
    ],
  },

  consent_required: {
    title: 'You cannot continue without sharing your information',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showNHSAppDetails: true,
        },
      },
    ],
  },

  'consent-not-given-nhs': {
    title: 'You cannot continue without sharing your information',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showNHSAppDetails: true,
        },
      },
    ],
  },

  'nhs-app-sso-session-expired': {
    title: 'Your sign in has expired',
    messageNhsAppWeb:
      'Your sign in has expired. This happens if the page was reloaded, or if too long passed while you were signing in.\n\n' +
      'Close this tab and start your request again from the NHS App.',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showNHSAppVerificationDetails: false,
          showNHSAppDetails: true,
        },
      },
    ],
  },

  'medicus-sso-generic': {
    title: 'There is a problem with the service',
    messageNhsAppWeb:
      'This may be a temporary problem. Close this tab and try logging in again. If you cannot log in, try again later.',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showNHSAppVerificationDetails: false,
          showNHSAppDetails: true,
        },
      },
    ],
  },

  'patient-portal-form-closed': {
    title: 'Online requests are now closed',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showOpeningTimes: true,
          showEmergencyCallSurgery: true,
          showOutOfHours111: true,
          showEmergency999: true,
        },
      },
    ],
  },

  'logged-in-patient-not-found': {
    title: 'Your record could not be found',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showCallSurgeryInstead: true,
        },
      },
    ],
  },

  'repeat-medication-request-form-unavailable': {
    title: 'Temporarily Unavailable',
    requestType: 'repeat-prescription',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showEmergencyCallSurgery: true,
        },
      },
    ],
  },

  'medical-request-form-unavailable': {
    title: 'Temporarily Unavailable',
    requestType: 'medical',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showOpeningTimes: true,
          showEmergencyCallSurgery: true,
        },
      },
    ],
  },

  'admin-request-form-unavailable': {
    title: 'Temporarily Unavailable',
    requestType: 'admin',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showOpeningTimes: true,
          showEmergencyCallSurgery: true,
        },
      },
    ],
  },

  'unable-to-send-verification-code': {
    title: 'Your verification code requests have been limited',
  },

  'organisation-not-configured-to-send-verification-code': {
    title: 'Unable to proceed',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showCallSurgeryInstead: true,
        },
      },
    ],
  },

  'incorrect-verification-code-input': {
    title: 'Form validation error below verification code input.',
    redirectToError: false,
    details: 'Error details',
  },

  invalid_request: {
    title: 'There was a problem with NHS login',
    redirectToError: true,
    details: 'Error details',
    route: '',
  },

  'verification-code-expired': {
    title: 'Form validation error below verification code input.',
    redirectToError: false,
  },

  'too-many-verification-code-failed-attempts': {
    title: 'Your verification attempts have been limited',
  },

  'cannot-validate-multiple-verification-codes': {
    title: 'This code has already been used',
    showBackButton: false,
  },

  'verification-code-not-found': {
    title: 'There is a problem with your verification',
  },

  'patient-has-no-email-address-or-mobile-number': {
    title: 'Unable to proceed',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showCallSurgeryInstead: true,
        },
      },
    ],
  },

  'proxy-patient-has-no-email-address-or-mobile-number': {
    title: 'Unable to proceed',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showCallSurgeryInstead: true,
        },
      },
    ],
  },

  'patient-has-no-email-address': {
    title: 'Unable to proceed',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showCallSurgeryInstead: true,
        },
      },
    ],
  },

  'patient-has-invalid-email-address': {
    title: 'Unable to proceed',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showCallSurgeryInstead: true,
        },
      },
    ],
  },

  'patient-has-no-mobile-number': {
    title: 'Unable to proceed',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showCallSurgeryInstead: true,
        },
      },
    ],
  },

  'patient-has-invalid-mobile-number': {
    title: 'Unable to proceed',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showCallSurgeryInstead: true,
        },
      },
    ],
  },

  'patient-has-invalid-email-address-and-mobile-number': {
    title: 'Unable to proceed',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showCallSurgeryInstead: true,
        },
      },
    ],
  },

  'unknown-error-authenticating-nhs-login-with-client': {
    title: 'There is a problem with NHS login',
  },

  'nhs-login-logged-in-patient-not-found': {
    title: 'Your record could not be found',
  },

  'single-patient-not-found': {
    title: 'Your record could not be found',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showCallSurgeryInstead: true,
        },
      },
    ],
  },

  'no-active-patient-found': {
    title: 'Your record could not be found',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showCallSurgeryInstead: true,
        },
      },
    ],
  },

  'user-session-invalid': {
    title: 'Error',
  },

  'sso-user-session-invalid': {
    title: 'There is a problem with the service',
    messageNhsAppWeb:
      'This may be a temporary problem. Go back and try logging in again. If you cannot log in, try again later.',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showNHSAppDetails: true,
        },
      },
    ],
  },

  'request-submission-patient-details-not-complete': {
    title: 'Request submission failed',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showCallSurgeryInstead: true,
        },
      },
    ],
  },

  'no-repeat-medication-found': {
    title: 'Unable to proceed',
  },

  'unsupported-file-type': {
    title: 'Unsupported file type',
  },

  'eps-unavailable-exception': {
    title: 'EPS unavailable',
  },

  'nhs-login-user-verification-level-too-low': {
    title: 'Your NHS login identity is not verified',
  },

  'nhs-login-sso-user-verification-level-too-low': {
    title: 'Your NHS login identity is not verified',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showNHSAppDetails: true,
          showNHSAppVerificationDetails: true,
        },
      },
    ],
  },

  unauthorized_client: {
    title: 'There was a problem with NHS login',
  },

  access_denied: {
    title: 'You cannot continue without sharing your information',
    messageNhsAppWeb:
      'The service provider needs your NHS login information to verify your identity.\n' +
      'To continue, close this tab then go back and agree to share your NHS login information. ',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showNHSAppVerificationDetails: false,
          showNHSAppDetails: true,
        },
      },
    ],
  },

  identity_proofing_level: {
    title: 'Your NHS login identity is not verified',
    messageNhsAppWeb:
      'It’s not possible to submit your request online with NHS login because your identity isn’t verified. ',
    components: [
      {
        name: 'mpf-tenant-info',
        props: {
          showNHSAppDetails: true,
          showNHSAppVerificationDetails: true,
        },
      },
    ],
  },
};
